/**
 * Map looks — the colours the geographic maps are drawn in.
 *
 * Each basemap is given the palette of the matching Mapbox style, so the maps read the way
 * operators who have used Mapbox-based tools expect:
 *
 *  · `streets`   — Map.       Mapbox Streets: warm off-white land, bright blue water, white
 *                             streets with blue-grey casings, orange motorways, yellow trunks.
 *  · `outdoors`  — Terrain.   Mapbox Outdoors: pale tan land, stronger greens, hill shading.
 *  · `satellite` — Satellite. Mapbox Satellite Streets: imagery with translucent roads and
 *                             white labels on a dark halo drawn over it.
 *
 * Only the COLOURS are Mapbox's. The data is still ours — the offline Protomaps archive, or
 * the keyless OpenFreeMap tiles — so nothing here needs a Mapbox account or token, and the
 * offline map keeps working with no internet.
 *
 * Two vector schemas reach this file, and each is recoloured its own way:
 *  · Protomaps (offline `/map-tiles/`)       → a `Flavor` for `@protomaps/basemaps`.
 *  · OpenMapTiles (OpenFreeMap "liberty")    → the fetched style, repainted layer by layer.
 */
import type { Flavor } from '@protomaps/basemaps';
import type * as MapLibre from 'maplibre-gl';

export type MapLook = 'streets' | 'outdoors' | 'satellite';

type Layer = MapLibre.LayerSpecification;
type Style = MapLibre.StyleSpecification;

/** What one look paints. Road fields are [fill, casing]. */
interface Palette {
  land: string;
  residential: string;
  park: string;
  wood: string;
  grass: string;
  sand: string;
  ice: string;
  hospital: string;
  school: string;
  cemetery: string;
  pitch: string;
  aeroway: string;
  runway: string;
  water: string;
  building: string;
  buildingOutline: string;
  motorway: [string, string];
  trunk: [string, string];
  primary: [string, string];
  street: [string, string];
  service: [string, string];
  path: [string, string];
  rail: string;
  boundary: string;
  placeText: string;
  placeHalo: string;
  roadText: string;
  roadHalo: string;
  waterText: string;
  waterHalo: string;
  poiText: string;
  poiHalo: string;
}

const STREETS: Palette = {
  land: '#f2efe9',
  residential: '#f2efe9',
  park: '#c8e3ad',
  wood: '#bddca0',
  grass: '#d3e8bf',
  sand: '#f1e9cf',
  ice: '#f4f8fa',
  hospital: '#f4dbe2',
  school: '#efe8cf',
  cemetery: '#d9e6c8',
  pitch: '#cfe5b8',
  aeroway: '#e6e3ec',
  runway: '#d8d4e0',
  water: '#75cff0',
  building: '#ece7e0',
  buildingOutline: '#ddd6cc',
  motorway: ['#ff9f80', '#e6795a'],
  trunk: ['#fdc65b', '#e3a63c'],
  primary: ['#ffffff', '#d4d7e3'],
  street: ['#ffffff', '#d9dbe5'],
  service: ['#ffffff', '#dfe0e8'],
  path: ['#d5cfc4', '#ffffff'],
  rail: '#bdbcc2',
  boundary: '#9b97a8',
  placeText: '#1f1f24',
  placeHalo: '#ffffff',
  roadText: '#55555c',
  roadHalo: '#ffffff',
  waterText: '#2f7ea8',
  waterHalo: 'rgba(255,255,255,0.75)',
  poiText: '#66666e',
  poiHalo: '#ffffff',
};

// Deliberately far from Streets: tan ground, saturated vegetation, darker water, quieter
// streets — the terrain, not the street grid, is what this view is for.
const OUTDOORS: Palette = {
  ...STREETS,
  land: '#e8e0c4',
  residential: '#e0d6bb',
  park: '#a8d38a',
  wood: '#8fc274',
  grass: '#c2dc9c',
  sand: '#ecdcae',
  cemetery: '#bcd5a2',
  pitch: '#b5d892',
  water: '#5fb0e0',
  building: '#d6ccb4',
  buildingOutline: '#c4b89c',
  trunk: ['#f6d06a', '#d9a944'],
  primary: ['#fbf7ea', '#c2b897'],
  street: ['#f8f4e6', '#cdc4a6'],
  service: ['#f6f1e2', '#d3caad'],
  path: ['#9a805c', '#f1ead6'],
  boundary: '#7f7560',
  placeText: '#2e2a20',
  roadText: '#5c5440',
  roadHalo: '#f3eedd',
  waterText: '#1f6690',
};

const SATELLITE: Palette = {
  ...STREETS,
  // Casings are not drawn on imagery (see SATELLITE_KEEPS) — only the fill colours matter.
  motorway: ['rgba(255,205,130,0.85)', 'transparent'],
  trunk: ['rgba(255,226,160,0.75)', 'transparent'],
  primary: ['rgba(255,255,255,0.55)', 'transparent'],
  street: ['rgba(255,255,255,0.28)', 'transparent'],
  service: ['rgba(255,255,255,0.18)', 'transparent'],
  path: ['rgba(255,255,255,0.22)', 'transparent'],
  rail: 'rgba(255,255,255,0.3)',
  boundary: 'rgba(255,255,255,0.45)',
  placeText: '#ffffff',
  placeHalo: 'rgba(0,0,0,0.75)',
  roadText: '#ffffff',
  roadHalo: 'rgba(0,0,0,0.75)',
  waterText: '#cdeeff',
  waterHalo: 'rgba(0,0,0,0.6)',
  poiText: '#ffffff',
  poiHalo: 'rgba(0,0,0,0.75)',
};

const PALETTES: Record<MapLook, Palette> = { streets: STREETS, outdoors: OUTDOORS, satellite: SATELLITE };

// ── Protomaps (offline tiles) ───────────────────────────────────────────────

/** The Streets look as a Protomaps flavor, on top of `base` (the flavor the sprites are for). */
export function streetsFlavor(base: Flavor): Flavor {
  const p = STREETS;
  const casing = p.street[1];
  return {
    ...base,
    background: p.land,
    earth: p.land,
    park_a: p.park,
    park_b: p.park,
    hospital: p.hospital,
    industrial: '#ebe6e8',
    school: p.school,
    wood_a: p.wood,
    wood_b: p.wood,
    pedestrian: '#f5f3ef',
    scrub_a: p.grass,
    scrub_b: p.grass,
    glacier: p.ice,
    sand: p.sand,
    beach: p.sand,
    aerodrome: p.aeroway,
    runway: p.runway,
    water: p.water,
    zoo: p.park,
    military: '#e8e2e2',
    tunnel_other_casing: casing,
    tunnel_minor_casing: casing,
    tunnel_link_casing: casing,
    tunnel_major_casing: casing,
    tunnel_highway_casing: p.motorway[1],
    tunnel_other: '#f7f7f7',
    tunnel_minor: '#f7f7f7',
    tunnel_link: '#f7f7f7',
    tunnel_major: '#f7f7f7',
    tunnel_highway: '#ffc9b5',
    pier: '#e6e2dc',
    buildings: p.building,
    minor_service_casing: p.service[1],
    minor_casing: casing,
    link_casing: p.primary[1],
    major_casing_late: p.primary[1],
    highway_casing_late: p.motorway[1],
    other: p.service[0],
    minor_service: p.service[0],
    minor_a: p.street[0],
    minor_b: p.street[0],
    link: p.primary[0],
    major_casing_early: p.primary[1],
    major: p.primary[0],
    highway_casing_early: p.motorway[1],
    highway: p.motorway[0],
    railway: p.rail,
    boundaries: p.boundary,
    bridges_other_casing: casing,
    bridges_minor_casing: casing,
    bridges_link_casing: p.primary[1],
    bridges_major_casing: p.primary[1],
    bridges_highway_casing: p.motorway[1],
    bridges_other: p.service[0],
    bridges_minor: p.street[0],
    bridges_link: p.primary[0],
    bridges_major: p.primary[0],
    bridges_highway: p.motorway[0],
    roads_label_minor: p.roadText,
    roads_label_minor_halo: p.roadHalo,
    roads_label_major: p.roadText,
    roads_label_major_halo: p.roadHalo,
    ocean_label: p.waterText,
    subplace_label: '#5f5f66',
    subplace_label_halo: p.placeHalo,
    city_label: p.placeText,
    city_label_halo: p.placeHalo,
    state_label: '#8a8794',
    state_label_halo: p.placeHalo,
    country_label: '#4a4852',
    address_label: '#8a8a90',
    address_label_halo: p.placeHalo,
    landcover: {
      grassland: '#dcecc9',
      barren: '#efe9d8',
      urban_area: '#ebe7e1',
      farmland: '#e5edd2',
      glacier: '#ffffff',
      scrub: '#dfeacb',
      forest: '#c9e2b0',
    },
  };
}

// ── OpenMapTiles (OpenFreeMap "liberty") ────────────────────────────────────

/** What a liberty layer draws, from its id. null = left exactly as liberty draws it. */
type Role =
  | 'land' | 'relief' | 'residential' | 'park' | 'wood' | 'grass' | 'sand' | 'ice' | 'wetland'
  | 'hospital' | 'school' | 'cemetery' | 'pitch' | 'aeroway' | 'runway' | 'water' | 'waterway'
  | 'roadArea' | 'road' | 'rail' | 'building' | 'boundary'
  | 'placeText' | 'roadText' | 'waterText' | 'poiText' | 'roadSymbol';

function roleOf(id: string): Role | null {
  if (id === 'background') return 'land';
  if (id === 'natural_earth') return 'relief';
  if (id.startsWith('park')) return 'park';
  if (id === 'landuse_residential') return 'residential';
  if (id === 'landcover_wood') return 'wood';
  if (id === 'landcover_grass') return 'grass';
  if (id === 'landcover_sand') return 'sand';
  if (id === 'landcover_ice') return 'ice';
  if (id === 'landcover_wetland') return 'wetland';
  if (id === 'landuse_hospital') return 'hospital';
  if (id === 'landuse_school') return 'school';
  if (id === 'landuse_cemetery') return 'cemetery';
  if (id === 'landuse_pitch' || id === 'landuse_track') return 'pitch';
  if (id === 'aeroway_fill') return 'aeroway';
  if (id.startsWith('aeroway_')) return 'runway';
  if (id === 'water') return 'water';
  if (/^waterway_(tunnel|river|other)$/.test(id)) return 'waterway';
  if (id === 'road_area_pattern') return 'roadArea';
  if (id.startsWith('road_one_way') || id.includes('shield')) return 'roadSymbol';
  if (/^(road|tunnel|bridge)_.*rail/.test(id)) return 'rail';
  if (/^(road|tunnel|bridge)_/.test(id)) return 'road';
  if (id.startsWith('building')) return 'building';
  if (id.startsWith('boundary')) return 'boundary';
  if (id.startsWith('label_')) return 'placeText';
  if (id.startsWith('highway-name')) return 'roadText';
  if (id.startsWith('water_name') || id === 'waterway_line_label') return 'waterText';
  if (id.startsWith('poi') || id === 'airport') return 'poiText';
  return null;
}

/** Fill and casing of one road layer. `trunk_primary` and `link` carry two classes in one layer. */
function roadColor(id: string, p: Palette): unknown {
  const pick = (r: [string, string]) => (id.includes('casing') ? r[1] : r[0]);
  if (id.includes('motorway')) return pick(p.motorway);
  if (id.includes('path_pedestrian')) return pick(p.path);
  if (id.includes('service_track')) return pick(p.service);
  if (id.includes('trunk_primary') || /_link(_casing)?$/.test(id)) {
    return ['match', ['get', 'class'], 'motorway', pick(p.motorway), 'trunk', pick(p.trunk), pick(p.primary)];
  }
  if (id.includes('secondary_tertiary')) return pick(p.primary);
  return pick(p.street); // minor, street
}

/** Roles a satellite overlay keeps — everything else would paint over the imagery. */
const SATELLITE_KEEPS = new Set<Role>(['road', 'rail', 'roadSymbol', 'boundary', 'placeText', 'roadText', 'waterText', 'poiText']);

function paintFor(role: Role, id: string, layer: Layer, p: Palette, look: MapLook): Record<string, unknown> | null {
  const fill: Partial<Record<Role, string>> = {
    residential: p.residential, park: p.park, wood: p.wood, grass: p.grass, sand: p.sand, ice: p.ice,
    wetland: p.grass, hospital: p.hospital, school: p.school, cemetery: p.cemetery, pitch: p.pitch,
    aeroway: p.aeroway, water: p.water,
  };
  switch (role) {
    case 'land':
      return { 'background-color': p.land };
    case 'runway':
      return { 'line-color': p.runway };
    case 'waterway':
      return { 'line-color': p.water };
    case 'road':
      return layer.type === 'line' ? { 'line-color': roadColor(id, p) } : null;
    case 'rail':
      return { 'line-color': p.rail };
    case 'building':
      return { 'fill-color': p.building, 'fill-outline-color': p.buildingOutline };
    case 'boundary':
      return { 'line-color': p.boundary };
    case 'placeText':
    case 'roadText':
    case 'waterText':
    case 'poiText': {
      const [text, halo] = {
        placeText: [p.placeText, p.placeHalo],
        roadText: [p.roadText, p.roadHalo],
        waterText: [p.waterText, p.waterHalo],
        poiText: [p.poiText, p.poiHalo],
      }[role];
      // On imagery the halo is what makes a label legible at all.
      return look === 'satellite'
        ? { 'text-color': text, 'text-halo-color': halo, 'text-halo-width': 1.4, 'text-halo-blur': 0.5 }
        : { 'text-color': text, 'text-halo-color': halo };
    }
    default: {
      const c = fill[role];
      if (!c) return null;
      // `park_outline` is a line; every other painted role here is a fill.
      return layer.type === 'line' ? { 'line-color': c } : { 'fill-color': c };
    }
  }
}

/**
 * OpenStreetMap areas the OpenMapTiles build files as WATER that are not water, by TILE feature
 * id — the OSM id × 10, plus 3 for a relation (2 for a way). That id is on the area in `water`
 * and on its name in `water_name` alike, so one entry removes both the fill and the label.
 *
 * The build turns any polygon with a `water=*` tag into a water feature, so one stray tag on a
 * big boundary floods it. Mapbox's own data ignores such tags, which is why the same place looks
 * right on Mapbox-based sites and like a lake on ours. Fix the tag on openstreetmap.org too —
 * the entry can go once OpenFreeMap's weekly build has picked the correction up.
 *
 *  · 90784683 — relation 9078468 "Girnar WLS", Junagadh: `natural=wood` with a stray
 *               `water=pond` (2025-05). Drew the whole sanctuary as a pond, labelled as a lake.
 */
const NOT_WATER_FEATURE_IDS = [90784683];

/** Drop the `NOT_WATER_FEATURE_IDS` areas, and their names, from an OpenMapTiles style. */
export function withoutFalseWater(style: Style): Style {
  const exclude = ['!', ['in', ['id'], ['literal', NOT_WATER_FEATURE_IDS]]];
  return {
    ...style,
    layers: style.layers.map((layer) => {
      const sourceLayer = (layer as { 'source-layer'?: string })['source-layer'];
      if (sourceLayer !== 'water' && sourceLayer !== 'water_name') return layer;
      const filter = (layer as { filter?: unknown }).filter;
      return { ...layer, filter: filter ? ['all', filter, exclude] : exclude } as Layer;
    }),
  };
}

/** Repaint a liberty style in `look`. `sourceId` renames its vector source (an overlay must be
 *  named `overlay-…` so its tile failures are not counted against the imagery underneath). */
export function restyleOpenMapTiles(style: Style, look: MapLook, sourceId = 'openmaptiles'): Style {
  const p = PALETTES[look];
  const layers: Layer[] = [];
  for (const layer of withoutFalseWater(style).layers) {
    const role = roleOf(layer.id);
    // Natural Earth's shaded relief tints the low zooms green and brown: right for Terrain,
    // where it is the relief you see before the hill shading takes over, wrong anywhere else.
    if (role === 'relief' && look !== 'outdoors') continue;
    // Liberty hands buildings to a 3-D extrusion from z14. These maps are always flat and
    // north-up, where extrusions only show as dark slivers; Mapbox draws them flat.
    if (layer.type === 'fill-extrusion') continue;
    if (look === 'satellite' && (!role || !SATELLITE_KEEPS.has(role) || layer.id.includes('casing'))) continue;
    // A paint block is replaced key by key, so liberty's opacities and widths survive.
    const paint = role ? paintFor(role, layer.id, layer, p, look) : null;
    const next = { ...layer, paint: { ...(layer as { paint?: object }).paint, ...paint } } as Layer;
    if (role === 'building') delete next.maxzoom; // the flat layer carries on past z14
    if ('source' in next && next.source === 'openmaptiles') (next as { source: string }).source = sourceId;
    layers.push(next);
  }
  const sources = { ...style.sources };
  if (look !== 'outdoors') delete sources.ne2_shaded;
  if (sourceId !== 'openmaptiles') {
    sources[sourceId] = sources.openmaptiles;
    delete sources.openmaptiles;
  }
  return { ...style, sources, layers };
}

/** Public elevation tiles (Mapzen Terrarium on AWS Open Data) — keyless, CORS-open. */
const DEM_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

/**
 * Hill shading for the Outdoors look, slotted above the land cover and under the water and
 * roads, the way Mapbox Outdoors layers it. `sourceId` should be an `overlay-…` name: lost
 * shading is cosmetic and must not declare the whole map unavailable.
 */
export function withHillshade(style: Style, sourceId: string): Style {
  const layers = [...style.layers];
  const at = layers.findIndex((l) => l.id.startsWith('water') || l.id.startsWith('road') || l.id.startsWith('tunnel'));
  layers.splice(at < 0 ? layers.length : at, 0, {
    id: 'hillshade',
    type: 'hillshade',
    source: sourceId,
    paint: {
      'hillshade-shadow-color': '#473820',
      'hillshade-highlight-color': '#fffdf5',
      'hillshade-accent-color': '#5e4c2e',
      'hillshade-exaggeration': 0.6,
    },
  } as Layer);
  return {
    ...style,
    sources: {
      ...style.sources,
      [sourceId]: {
        type: 'raster-dem',
        tiles: [DEM_TILES],
        tileSize: 256,
        maxzoom: 15,
        encoding: 'terrarium',
        attribution: 'Elevation &copy; <a href="https://registry.opendata.aws/terrain-tiles/">Mapzen, AWS Open Data</a>',
      },
    },
    layers,
  };
}
